import { Component, Input, OnInit, OnDestroy, EventEmitter, Output, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { FormNotesCustomersComponent } from 'src/app/modules/customers/components/notes-customers-form/notes-customers-form.component';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { HttpParams } from '@angular/common/http';
import { ProjectsRoundTestlistComponent } from '../projects-round-testlist/projects-round-testlist.component';
import { TestFailureModalService, TestFailureModalContext } from 'src/app/services/test-failure-modal.service';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { TestLogModalService } from 'src/app/services/test-log-modal.service';
import { CloneIssueDestinationStep, CloneIssueModalService } from 'src/app/services/clone-issue-modal.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { LoaderConfig } from 'src/app/components/widgets/loader/loader.config';
import { CommonService } from 'src/app/services/common.service';
import { ProjectsHistoryLogsComponent } from '../projects-history-logs/projects-history-logs.component';
import { AuditLog } from 'src/app/models/audit-log.model';
import { Observable, Subject, forkJoin, of, takeUntil, tap } from 'rxjs';

interface Rule {
  id: string;
  title: string;
  content: string;
  expanded?: boolean;
}

interface ProjectTestListStep {
  projectTestlistStepId: number;
  testlistStep: {
    testlistStepId: number;
    type: 'WITH_EVIDENCE' | 'WITHOUT_EVIDENCE';
    text: string;
    position: number;
  };
  projectIssues?: any[];
  status: 'PASSED' | 'FAILED' | 'NOT_STARTED' | 'SKIPPED';
  lastAuditLog?: AuditLog | null;
}


interface MacroTest {
  id: number;
  name: string;
  description?: string;
  estimated_time: number;
  individual_tests: any[];
  individual_tests_without_evidence: any[];
  individual_tests_with_evidence: any[];
  rules?: Rule[];
  rulesLoaded?: boolean;
  rulesLoading?: boolean;
  status?: 'passed' | 'failed' | 'na' | 'pending' | null;
  reviewConfirmed?: boolean;
  projectTestlistId?: number;
  testlistId?: number;
  testerProjectTeam?: any;
  projectChecklistId?: number;
  apiResult?: string;
  testlist?: any;
  result?: 'PASSED' | 'FAILED' | 'SKIPPED' | 'PENDING' | 'LIGHT_PASSED' | 'LIGHT_FAILED' | null;
  evidenceFiles?: any[];
  projectIssueId?: number;
  stepsLoaded?: boolean;
  stepsLoading?: boolean;
  stepsRequestPending?: boolean;
  resultButtons?: {
    passedEnabled: boolean;
    failedEnabled: boolean;
    naEnabled: boolean;
    pendingEnabled: boolean;
  };
  reviewedSynced?: boolean;
  stagedResult?: 'PASSED' | 'FAILED' | 'SKIPPED' | 'PENDING' | 'LIGHT_PASSED' | 'LIGHT_FAILED' | null;
}

interface ChecklistOption {
  key: number;
  value: string;
  result?: string;
  dotClass?: string;
}

interface BatchOption {
  key: number;
  value: string;
  checklists?: any[];
  roundData?: any;
}

interface TesterOption {
  key: string | number;
  value: string;
}

@Component({
  selector: 'projects-testlist',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectFieldComponent,
    ProjectsRoundTestlistComponent,
    LoaderComponent,
    FormNotesCustomersComponent,
    ProjectsHistoryLogsComponent
  ],
  templateUrl: './projects-testlist.component.html',
  styleUrl: './projects-testlist.component.scss'
})
export class ProjectsTestlistComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() projectId?: string;
  @Input() jurisdictionId?: string | number | null;
  @Output() onTestlistUpdated = new EventEmitter<any>();
  @Output() canEditChange = new EventEmitter<boolean>();

  canEditTestlist: boolean = false;
  canEditReviewCheckbox: boolean = false;
  canViewReviewCheckbox: boolean = false;
  canUploadEvidence: boolean = false;
  restrictRoundToClientDeclaration: boolean = false;

  form: FormGroup;
  batches: BatchOption[] = [];
  checklists: ChecklistOption[] = [];
  allChecklists: ChecklistOption[] = [];
  testers: TesterOption[] = [];
  selectedBatchId: number | null = null;
  selectedTesterId: number | null = null;
  selectedMacroTest: MacroTest | null = null;
  selectedIndividualTest: any | null = null;
  macroTests: MacroTest[] = [];
  
  globalEvidenceFiles: any[] = [];
  globalEvidenceFileIds: Map<number, number> = new Map();
  
  selectedFileToDelete: number | null = null;
  confirmDialogAction: 'deleteGlobal' | 'deleteFailure' | null = null;
  isFailureFileDeletion: boolean = false;
  
  failedTest: any | null = null;
  failureCommentForm: FormGroup;
  failureEvidenceFiles: File[] = [];
  failureEvidenceFileIds: Map<number, number> = new Map();
  failureExistingEvidenceFiles: any[] = [];
  failureExistingEvidenceFileIds: Map<number, number> = new Map();
  isLoadingFailureEvidences: boolean = false;
  failureModalConfirmed = false;
  pendingFailureTestId: number | null = null;
  failedTestWasAlreadyFailed: boolean = false; 

  private cloneIssueSource: {
    projectTestlistId: number;
    projectTestlistStepId: number;
    testTitle: string;
  } | null = null;
  
  testlistNotes: any[] = [];
  testStatusEditing: { [key: number]: 'passed' | 'failed' | 'na' | 'pending' | null } = {};
  testStatusChanges: Set<number> = new Set();
  

  teamConflictError: boolean = false;
  teamConflictErrorMessage: string = 'The customer has not set his choice about team conflict or there is conflict in the team.\nCheck customer response in Team tab abount team conflict.';

  testLogModalVisible: boolean = false;
  selectedTestForLog: any = null;
  testLogs: any[] = [];
  isLoadingTestLogs: boolean = false;

  rulesCollapsed: boolean = true;

  canDeleteFailureFiles: boolean = false;
  private readonly FAILURE_FILE_DELETE_PROFILES = ['ADM1', 'CTO1'];

  stepsLoaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Loading test steps...',
    overlay: true
  };

  @ViewChild('mainContentRef') mainContentRef?: ElementRef<HTMLElement>;

  private statusResultToDotClass(result?: string): string {
    switch (result) {
      case 'PASSED':        return 'dot-passed';
      case 'FAILED':        return 'dot-failed';
      case 'PENDING':       return 'dot-pending';
      case 'LIGHT_PASSED':  return 'dot-passed-light';
      case 'LIGHT_FAILED':  return 'dot-failed-light';
      default:              return 'dot-none';
    }
  }

  shouldFlashTestLogIcon(test: any): boolean {
    return this.getTestStatusForDisplay(test) === 'failed';
  }

  constructor(
    private fb: FormBuilder,
    public projectsService: ProjectsService,
    private alertNotificationService: AlertNotificationService,
    private testFailureModalService: TestFailureModalService,
    private confirmationDialogService: ConfirmationDialogService,
    private testLogModalService: TestLogModalService,
    private cloneIssueModalService: CloneIssueModalService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private commonService: CommonService
  ) {
    this.form = this.fb.group({
      selectedBatch: ['', Validators.required],
      selectedChecklist: ['', Validators.required],
      selectedTester: ['']
    });
    
    this.failureCommentForm = this.fb.group({
      failureComment: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$.pipe(takeUntil(this.destroy$)).subscribe(profileCode => {
      this.canDeleteFailureFiles = this.FAILURE_FILE_DELETE_PROFILES.includes(profileCode ?? '');

      this.canEditTestlist = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TESTLIST_MODIFY', 'modify'
      );
      this.canEditReviewCheckbox = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TESTLIST_REVIEW_CHECKBOX', 'modify'
      );
      this.canViewReviewCheckbox = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TESTLIST_REVIEW_CHECKBOX_CONFIRM', 'view'
      );
      this.canUploadEvidence = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TESTLIST_EVIDENCE_UPLOAD', 'modify'
      );
      this.restrictRoundToClientDeclaration = !this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_TESTLIST_ROUND_FULL_ACCESS', 'modify'
      );
      this.canEditChange.emit(this.canEditTestlist);
    });

    if (this.projectId) {
      this.loadBatches();
      this.loadAllChecklists(this.jurisdictionId);
      this.loadTesters();
      this.checkTeamConflicts(Number(this.projectId));
    }
  }

  private loadTestlistNotes(macroTestId: number): void {
    if (!this.projectId) return;

    const params = new HttpParams().set('size', '9999');

    this.projectsService.listTestlistNotes(Number(this.projectId), macroTestId, { params })
      .subscribe({
        next: (response) => {
          this.testlistNotes = response?.data?.sectionNotes || [];
        },
        error: (error) => {
          console.error('Error loading testlist notes:', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cloneIssueModalService.close();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jurisdictionId'] && !changes['jurisdictionId'].firstChange && this.projectId) {
      this.loadAllChecklists(this.jurisdictionId);

      if (this.selectedBatchId) {
        this.loadChecklistsByBatch(this.selectedBatchId, this.jurisdictionId);
      }
    }
  }

  private loadBatches(): void {
    if (!this.projectId) return;

    this.projectsService.listProjectBatch(Number(this.projectId))
      .subscribe({
        next: (response) => {
          this.batches = response.data.projectBatches.map((batch: any) => ({
            key: batch.projectBatchId,
            value: batch.name,
            roundData: null
          }));
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading batches');
          console.error('Error:', error);
        }
      });
  }

  private loadAllChecklists(jurisdictionId?: string | number | null): void {
    if (!this.projectId) return;

    let params = new HttpParams().set('size', '9999');
    if (jurisdictionId !== null && jurisdictionId !== undefined && jurisdictionId !== '') {
      params = params.set('jurisdictionId', `${jurisdictionId}`);
    }

    this.projectsService.listProjectChecklists(Number(this.projectId), { params })
      .subscribe({
        next: (response) => {
          this.allChecklists = response.data.checklists.map((checklist: any) => ({
            key: checklist.checklistId,
            value: checklist.name
          }));
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading checklists');
          console.error('Error:', error);
        }
      });
  }

  private loadTesters(): void {
    if (!this.projectId) return;

    this.projectsService.listProjectTeams(Number(this.projectId))
      .subscribe({
        next: (response) => {
          this.testers = [
            { key: '', value: ' ' },
            ...(response.data?.projectTeams ?? []).map((team: any) => ({
              key: team.projectTeamId,
              value: `${team.employee.firstName} ${team.employee.lastName}`
            }))
          ];
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading testers');
          console.error('Error:', error);
          this.testers = [{ key: '', value: ' ' }];
        }
      });
  }

  private loadChecklistsByBatch(
    batchId: number,
    jurisdictionId?: string | number | null,
    preserveSelection: boolean = false,
    silent: boolean = false
  ): void {
    if (!this.projectId) return;

    let params = new HttpParams();
    if (jurisdictionId !== null && jurisdictionId !== undefined && jurisdictionId !== '') {
      params = params.set('jurisdictionId', `${jurisdictionId}`);
    }
    if (this.selectedTesterId) {
      params = params.set('projectTeamId', `${this.selectedTesterId}`);
    }

    const previousChecklistId = this.form.get('selectedChecklist')?.value;

    this.projectsService.listProjectBatchChecklist(Number(this.projectId), batchId, { params })
      .subscribe({
        next: (response) => {
          this.checklists = response.data.projectBatchChecklists.map((checklist: any) => ({
            key: checklist.projectChecklist.projectChecklistId,
            value:`${checklist.projectChecklist.projectWorkinglist.checklist.name} - ${checklist.projectChecklist.projectWorkinglist.name} - ${checklist.projectChecklist.projectWorkinglist.platform.name}`,
            result: checklist.projectChecklist.result,
            dotClass: this.statusResultToDotClass(checklist.projectChecklist.result)
          }));

          if (preserveSelection && !this.isChecklistStillAvailable(previousChecklistId)) {
            this.clearChecklistSelection();
          }
        },
        error: (error) => {
          console.error('Error:', error);

          if (silent) {
            return;
          }

          this.alertNotificationService.error('Error loading checklists for batch');
          this.checklists = [];

          if (preserveSelection) {
            this.clearChecklistSelection();
          }
        }
      });

    if (!preserveSelection) {
      this.form.patchValue({ selectedChecklist: '' });
    }
  }

  private isChecklistStillAvailable(checklistId: any): boolean {
    if (checklistId === null || checklistId === undefined || checklistId === '') return false;
    return this.checklists.some(checklist => String(checklist.key) === String(checklistId));
  }

  private clearChecklistSelection(): void {
    this.form.patchValue({ selectedChecklist: '' });
    this.macroTests = [];
    this.selectedMacroTest = null;
    this.selectedIndividualTest = null;
  }

  private refreshChecklistOptions(): void {
    if (!this.projectId || !this.selectedBatchId) return;

    this.loadChecklistsByBatch(this.selectedBatchId, this.jurisdictionId, true, true);
  }

  private loadProjectTestlistStatusRounds(projectId: number, projectBatchId: number): void {
    this.projectsService.listProjectTestlistStatusRounds(projectId, projectBatchId)
      .subscribe({
        next: (response) => {
          const roundData = this.mapApiRoundDataToComponentRounds(response.data?.projectRoundGroupedInfo);
          this.saveRoundStateForCurrentBatch(roundData);
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading round information for batch');
          console.error('Error:', error);
        }
      });
  }

  private mapApiRoundDataToComponentRounds(apiRoundData: any): any[] {
    if (!apiRoundData) {
      return this.getDefaultRounds();
    }

    const rounds: any[] = [];

    rounds.push({
      id: 'round1',
      name: '1° Round',
      isActive: apiRoundData.status === 'TO_START' || apiRoundData.status === 'ROUND1_STARTED',
      states: [
        {
          id: 'round1-start',
          name: 'Started',
          type: 'start',
          status: this.getStateStatus('round1-start', apiRoundData),
          operatorName: apiRoundData.round1StartUser ? this.formatUserName(apiRoundData.round1StartUser) : undefined,
          completedAt: apiRoundData.round1StartTime ? new Date(apiRoundData.round1StartTime) : undefined
        },
        {
          id: 'round1-finish',
          name: 'Finished',
          type: 'finish',
          status: this.getStateStatus('round1-finish', apiRoundData),
          operatorName: apiRoundData.round1EndUser ? this.formatUserName(apiRoundData.round1EndUser) : undefined,
          completedAt: apiRoundData.round1EndTime ? new Date(apiRoundData.round1EndTime) : undefined
        }
      ]
    });

    rounds.push({
      id: 'client-declaration',
      name: 'Client declaration - All issues are fixed',
      isActive: apiRoundData.status === 'ROUND1_ENDED',
      states: [
        {
          id: 'client-go-ahead',
          name: 'Go ahead from the client',
          type: 'client-declaration',
          status: this.getStateStatus('client-go-ahead', apiRoundData),
          operatorName: apiRoundData.nextRoundUser ? this.formatUserName(apiRoundData.nextRoundUser) : undefined,
          completedAt: apiRoundData.nextRoundTime ? new Date(apiRoundData.nextRoundTime) : undefined
        }
      ]
    });

    rounds.push({
      id: 'round2',
      name: '2° Round',
      isActive: apiRoundData.status === 'CLIENT_CONFIRMED1' || apiRoundData.status === 'ROUND2_STARTED',
      states: [
        {
          id: 'round2-start',
          name: 'Started',
          type: 'start',
          status: this.getStateStatus('round2-start', apiRoundData),
          operatorName: apiRoundData.round2StartUser ? this.formatUserName(apiRoundData.round2StartUser) : undefined,
          completedAt: apiRoundData.round2StartTime ? new Date(apiRoundData.round2StartTime) : undefined
        },
        {
          id: 'round2-finish',
          name: 'Finished',
          type: 'finish',
          status: this.getStateStatus('round2-finish', apiRoundData),
          operatorName: apiRoundData.round2EndUser ? this.formatUserName(apiRoundData.round2EndUser) : undefined,
          completedAt: apiRoundData.round2EndTime ? new Date(apiRoundData.round2EndTime) : undefined
        }
      ]
    });

    return rounds;
  }

  private getStateStatus(stateId: string, apiRoundData: any): 'disabled' | 'active' | 'completed' {
    const status = apiRoundData.status;

    switch (stateId) {
      case 'round1-start':
        if (status === 'TO_START') return 'active';
        if (['ROUND1_STARTED', 'ROUND1_ENDED', 'CLIENT_CONFIRMED1', 'ROUND2_STARTED', 'ROUND2_ENDED'].includes(status)) return 'completed';
        return 'disabled';

      case 'round1-finish':
        if (status === 'ROUND1_STARTED') return 'active';
        if (['ROUND1_ENDED', 'CLIENT_CONFIRMED1', 'ROUND2_STARTED', 'ROUND2_ENDED'].includes(status)) return 'completed';
        return 'disabled';

      case 'client-go-ahead':
        if (status === 'ROUND1_ENDED') return 'active';
        if (['CLIENT_CONFIRMED1', 'ROUND2_STARTED', 'ROUND2_ENDED'].includes(status)) return 'completed';
        return 'disabled';

      case 'round2-start':
        if (status === 'CLIENT_CONFIRMED1') return 'active';
        if (['ROUND2_STARTED', 'ROUND2_ENDED'].includes(status)) return 'completed';
        return 'disabled';

      case 'round2-finish':
        if (status === 'ROUND2_STARTED') return 'active';
        if (status === 'ROUND2_ENDED') return 'completed';
        return 'disabled';

      default:
        return 'disabled';
    }
  }

  private formatUserName(user: any): string {
    if (user.firstName && user.lastName) {
      return `${user.lastName} ${user.firstName}`;
    }
    return user.userCode || 'Unknown';
  }

  private getDefaultRounds(): any[] {
    return [
      {
        id: 'round1',
        name: '1° Round',
        isActive: true,
        states: [
          {
            id: 'round1-start',
            name: 'Started',
            type: 'start',
            status: 'active'
          },
          {
            id: 'round1-finish',
            name: 'Finished',
            type: 'finish',
            status: 'disabled'
          }
        ]
      },
      {
        id: 'client-declaration',
        name: 'Client declaration - All issues are fixed',
        isActive: false,
        states: [
          {
            id: 'client-go-ahead',
            name: 'Go ahead from the client',
            type: 'client-declaration',
            status: 'disabled'
          }
        ]
      },
      {
        id: 'round2',
        name: '2° Round',
        isActive: false,
        states: [
          {
            id: 'round2-start',
            name: 'Started',
            type: 'start',
            status: 'disabled'
          },
          {
            id: 'round2-finish',
            name: 'Finished',
            type: 'finish',
            status: 'disabled'
          }
        ]
      }
    ];
  }

  private loadTestlists(projectChecklistId?: number): void {
    if (!this.projectId) return;

    if (!projectChecklistId) {
      this.macroTests = [];
      return;
    }

    this.projectsService.listProjectTestlists(Number(this.projectId), projectChecklistId)
      .subscribe({
        next: (response) => {
          this.macroTests = response.data.projectTestlists.map((projectTestlist: any) => {
            const macroTest: MacroTest = {
              id: projectTestlist.projectTestlistId,
              name: projectTestlist.testlist?.name,
              description: `Status: ${projectTestlist.status}, Result: ${projectTestlist.result}`,
              estimated_time: 0,
              individual_tests: [],
              individual_tests_without_evidence: [],
              individual_tests_with_evidence: [],
              rules: [],
              rulesLoaded: false,
              rulesLoading: false,
              status: this.mapApiResultToComponentStatus(projectTestlist.result),
              reviewConfirmed: projectTestlist.reviewed || false,
              projectTestlistId: projectTestlist.projectTestlistId,
              testlistId: projectTestlist.testlist?.testlistId,
              testerProjectTeam: projectTestlist.testerProjectTeam,
              projectChecklistId: projectTestlist.projectChecklist?.projectChecklistId,
              apiResult: projectTestlist.result,
              result: projectTestlist.result,
              testlist: projectTestlist.testlist,
              stepsLoaded: false,
              stepsLoading: false,
              resultButtons: projectTestlist.resultButtons,
              reviewedSynced: projectTestlist.reviewed || false
            };

                return macroTest;
              });
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading testlists');
          console.error('Error:', error);
        }
      });
  }

  private loadProjectTestListSteps(projectId: number, projectTestlistId: number, macroTest: MacroTest, showLoader: boolean = true): void {
        if (macroTest.stepsRequestPending) {
          return;
        }

        macroTest.stepsRequestPending = true;
        macroTest.stepsLoading = showLoader;
    this.projectsService.listProjectTestListSteps(projectId, projectTestlistId)
      .subscribe({
        next: (response) => {
          const projectTestlistSteps: ProjectTestListStep[] = response.data.projectTestlistSteps || [];
          
          const withoutEvidenceTests: any[] = [];
          const withEvidenceTests: any[] = [];

          projectTestlistSteps.forEach((step: ProjectTestListStep) => {
            const individualTest: any = {
              id: step.testlistStep.testlistStepId,
              projectTestlistStepId: step.projectTestlistStepId,
              title: step.testlistStep.text,
              description: step.testlistStep.text,
              verification_type: step.testlistStep.type === 'WITH_EVIDENCE' ? 'with_evidence' : 'without_evidence',
              status: this.mapApiStatusToComponentStatus(step.status),
              evidence_files: [],
              position: step.testlistStep.position,
              projectIssueId: step?.projectIssues?.[0]?.projectIssueId || null,
              lastUpdate: this.buildLastUpdateLabel(step.lastAuditLog)
            };

            if (step.testlistStep.type === 'WITHOUT_EVIDENCE') {
              withoutEvidenceTests.push(individualTest);
            } else if (step.testlistStep.type === 'WITH_EVIDENCE') {
              withEvidenceTests.push(individualTest);
            }
          });

          withoutEvidenceTests.sort((a, b) => a.position - b.position);
          withEvidenceTests.sort((a, b) => a.position - b.position);

          macroTest.individual_tests_without_evidence = withoutEvidenceTests;
          macroTest.individual_tests_with_evidence = withEvidenceTests;
          macroTest.individual_tests = [...withoutEvidenceTests, ...withEvidenceTests];
          macroTest.stepsLoaded = true;
          macroTest.stepsLoading = false;
          macroTest.stepsRequestPending = false;
        },
        error: (error) => {
          this.alertNotificationService.error(`Error loading test steps for testlist ${projectTestlistId}`);
          console.error('Error loading test steps:', error);
          macroTest.stepsLoading = false;
          macroTest.stepsRequestPending = false;
        }
      });
  }

  private buildLastUpdateLabel(auditLog?: AuditLog | null): string | null {
    if (!auditLog?.createTime || !auditLog.creatorUser) {
      return null;
    }

    return `${this.commonService.formatDate(new Date(auditLog.createTime))} - ${this.formatUserName(auditLog.creatorUser)}`;
  }

  private mapApiStatusToComponentStatus(apiStatus: string): 'passed' | 'failed' | 'na' | 'pending' {
    switch (apiStatus) {
      case 'PASSED':
        return 'passed';
      case 'FAILED':
        return 'failed';
      case 'SKIPPED':
        return 'na';
      case 'NOT_STARTED':
      default:
        return 'pending';
    }
  }

  private mapApiResultToComponentStatus(apiResult: string): 'passed' | 'failed' | 'na' | 'pending' | null {
    if (!apiResult) return null;
    
    switch (apiResult) {
      case 'PASSED':
        return 'passed';
      case 'FAILED':
        return 'failed';
      case 'SKIPPED':
        return 'na';
      case 'PENDING':
        return 'pending';
      case 'LIGHT_PASSED':
        return 'passed';
      case 'LIGHT_FAILED':
        return 'failed';
      default:
        return null;
    }
  }

  onBatchChange(value: string): void {
    const batchId = Number(value);
    this.selectedBatchId = batchId;
    this.loadChecklistsByBatch(batchId, this.jurisdictionId);
    this.selectedMacroTest = null;
    this.selectedIndividualTest = null;
    
    if (this.projectId && batchId) {
      this.loadProjectTestlistStatusRounds(Number(this.projectId), batchId);
    }
  }

  reloadWithJurisdiction(jurisdictionId?: string | number | null): void {
    this.loadAllChecklists(jurisdictionId);
    if (this.selectedBatchId) {
      this.loadChecklistsByBatch(this.selectedBatchId, jurisdictionId);
    }
  }

  private checkTeamConflicts(projectId: number): void {
    this.projectsService.listProjectTeamConfilts(projectId)
      .subscribe({
        next: (response) => {
          this.teamConflictError = false;
        },
        error: (error) => {
          if (error.status === 404) {
            this.teamConflictError = true;
          } else {
            console.error('Error checking team conflicts:', error);
            this.teamConflictError = false;
          }
        }
      });
  }

  onTesterChange(value: string): void {
    this.selectedTesterId = value ? Number(value) : null;

    if (this.selectedBatchId) {
      this.loadChecklistsByBatch(this.selectedBatchId, this.jurisdictionId, true);
    }
  }

  onChecklistChange(value: string): void {
    const projectChecklistId = Number(value);
    this.selectedMacroTest = null;
    this.selectedIndividualTest = null;
    if (projectChecklistId) {
      this.loadTestlists(projectChecklistId);
    }
  }

  selectMacroTest(macroTest: MacroTest): void {
    this.selectedMacroTest = macroTest;
    this.selectedIndividualTest = null;
    this.rulesCollapsed = true;
    this.scrollMainContentToTop();
    this.loadTestlistNotes(macroTest.id);

    if (macroTest.projectTestlistId) {
      this.loadProjectTestListSteps(
        Number(this.projectId),
        macroTest.projectTestlistId,
        macroTest
      );
      this.loadEvidenceFilesForTestlist(macroTest.projectTestlistId);
      this.loadMacroTestRules(macroTest);
      this.refreshMacroTestStatus(macroTest);
    }
  }

  private scrollMainContentToTop(): void {
    this.mainContentRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private loadMacroTestRules(macroTest: MacroTest): void {
    if (!this.projectId || !macroTest.projectTestlistId) return;
    if (macroTest.rulesLoading) return;

    macroTest.rulesLoading = true;
    this.projectsService.listProjectTestlistsRules(Number(this.projectId), macroTest.projectTestlistId)
      .subscribe({
        next: (response) => {
          const regulations: any[] = response?.data?.regulations ?? [];
          macroTest.rules = regulations.map((reg: any): Rule => ({
            id: String(reg.regulationId),
            title: reg.title ?? '',
            content: reg.details ?? '',
            expanded: false
          }));
          macroTest.rulesLoaded = true;
          macroTest.rulesLoading = false;
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading rules for testlist');
          console.error('Error loading rules:', error);
          macroTest.rulesLoading = false;
        }
      });
  }

  selectIndividualTest(individualTest: any): void {
    this.selectedIndividualTest = individualTest;
  }

  private mapMacroTestStatusToApiResult(
    status: 'passed' | 'failed' | 'na' | 'pending',
    currentResult?: MacroTest['result']
  ): 'PASSED' | 'FAILED' | 'SKIPPED' | 'PENDING' | 'LIGHT_PASSED' | 'LIGHT_FAILED' {
    switch (status) {
      case 'passed': return currentResult === 'LIGHT_PASSED' ? 'LIGHT_PASSED' : 'PASSED';
      case 'failed': return currentResult === 'LIGHT_FAILED' ? 'LIGHT_FAILED' : 'FAILED';
      case 'na': return 'SKIPPED';
      case 'pending': return 'PENDING';
    }
  }

  isMacroTestButtonDisabled(button: 'passed' | 'failed' | 'na' | 'pending'): boolean {
    const resultButtons = this.selectedMacroTest?.resultButtons;
    if (!resultButtons) {
      return false;
    }
    switch (button) {
      case 'passed': return !resultButtons.passedEnabled;
      case 'failed': return !resultButtons.failedEnabled;
      case 'na': return !resultButtons.naEnabled;
      case 'pending': return !resultButtons.pendingEnabled;
    }
  }

  setMacroTestStatus(status: 'passed' | 'failed' | 'na' | 'pending'): void {
    const macroTest = this.selectedMacroTest;
    if (!macroTest || !macroTest.projectTestlistId) {
      return;
    }

    macroTest.stagedResult = this.mapMacroTestStatusToApiResult(status, macroTest.result);
    macroTest.status = status;
  }

  private refreshMacroTestStatus(macroTest: MacroTest): void {
    if (!this.projectId || !macroTest.projectTestlistId) {
      return;
    }

    const previousResult = macroTest.result ?? null;

    this.projectsService.detailProjectTestlist(Number(this.projectId), macroTest.projectTestlistId)
      .subscribe({
        next: (response) => {
          const projectTestlist = response.data?.projectTestlist;
          if (!projectTestlist) {
            return;
          }
          macroTest.apiResult = projectTestlist.result;
          macroTest.result = projectTestlist.result;
          macroTest.status = this.mapApiResultToComponentStatus(projectTestlist.result);
          macroTest.reviewConfirmed = projectTestlist.reviewed ?? macroTest.reviewConfirmed;
          macroTest.reviewedSynced = projectTestlist.reviewed ?? macroTest.reviewedSynced;
          macroTest.resultButtons = projectTestlist.resultButtons ?? macroTest.resultButtons;
          macroTest.stagedResult = null;

          if ((projectTestlist.result ?? null) !== previousResult) {
            this.refreshChecklistOptions();
          }
        },
        error: (error) => {
          this.alertNotificationService.error('Error refreshing testlist status');
          console.error('Error refreshing testlist status:', error);
        }
      });
  }

  setIndividualTestStatus(testId: number, status: 'passed' | 'failed' | 'na' | 'pending'): void {
    if (this.selectedMacroTest) {
      const test = this.selectedMacroTest.individual_tests.find(t => t.id === testId);
      if (test) {
        if (status === 'failed') {
          this.failedTest = test;
          this.pendingFailureTestId = testId;
          this.failureModalConfirmed = false;
          this.failedTestWasAlreadyFailed = test.status === 'failed';
          this.openTestFailureModal();

          if (test.projectIssueId && this.failedTestWasAlreadyFailed) {
            this.loadFailureExistingEvidences(test.projectIssueId);
            this.loadFailureIssueDetails(test.projectIssueId);
          }
        } else {
          if (test.status === status) {
            return;
          }
          this.testStatusEditing[testId] = status;
          this.testStatusChanges.add(testId);
        }
      }
    }
  }

  setTestStatusFailedFromModal(testId: number): void {
    if (testId !== null && testId !== undefined) {
      this.testStatusEditing[testId] = 'failed';
      this.testStatusChanges.add(testId);
    }
  }

  saveTestStatus(testId: number): void {
    if (this.selectedMacroTest) {
      const test = this.selectedMacroTest.individual_tests.find(t => t.id === testId);
      if (test) {
        const newStatus = this.testStatusEditing[testId];
        
        if (!newStatus) {
          return;
        }

        if (newStatus === 'failed' && !this.failureModalConfirmed) {
          this.alertNotificationService.warning('Please complete the failure modal before saving');
          return;
        }

        test.status = newStatus;
        const macroTest = this.selectedMacroTest;

        if (test.projectTestlistStepId) {
          this.projectsService.updateProjectTestListSteps(
            Number(this.projectId),
            macroTest.projectTestlistId!,
            test.projectTestlistStepId,
            {
              status: this.mapComponentStatusToApiStatus(newStatus),
            }
          ).subscribe({
            next: (response) => {
              this.alertNotificationService.success(`Test marked as ${newStatus}`);
              delete this.testStatusEditing[testId];
              this.testStatusChanges.delete(testId);
              this.refreshMacroTestStatus(macroTest);
              this.loadProjectTestListSteps(Number(this.projectId), macroTest.projectTestlistId!, macroTest, false);
            },
            error: (error) => {
              test.status = null;
              this.alertNotificationService.error(`Error updating test status`);
              console.error('Error updating test status:', error);
              delete this.testStatusEditing[testId];
              this.testStatusChanges.delete(testId);
            }
          });
        } else {
          delete this.testStatusEditing[testId];
          this.testStatusChanges.delete(testId);
        }
        
        this.onTestlistUpdated.emit();
      }
    }
  }

  cancelTestStatusChange(testId: number): void {
    delete this.testStatusEditing[testId];
    this.testStatusChanges.delete(testId);
  }

  hasTestStatusChanges(testId: number): boolean {
    return this.testStatusChanges.has(testId);
  }

  openTestLogModal(test: any, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedTestForLog = test;
    this.testLogModalService.open(test.title, test.projectTestlistStepId ?? test.id);
  }

  closeTestLogModal(): void {
    this.testLogModalService.close();
    this.selectedTestForLog = null;
  }

  getLogStatusLabel(status: string): string {
    switch (status) {
      case 'PASSED': return 'Pass';
      case 'FAILED': return 'Fail';
      case 'SKIPPED': return 'N/A';
      default: return status;
    }
  }

  getLogStatusClass(status: string): string {
    switch (status) {
      case 'PASSED': return 'badge-passed';
      case 'FAILED': return 'badge-failed';
      case 'SKIPPED': return 'badge-na';
      default: return '';
    }
  }

  isTestStatusModified(testId: number): boolean {
    const hasEditing = this.testStatusEditing[testId] !== undefined && this.testStatusEditing[testId] !== null;
    const hasChanges = this.testStatusChanges.has(testId);
    return hasEditing || hasChanges;
  }
  getTestStatusForDisplay(test: any): 'passed' | 'failed' | 'na' | 'pending' | null {
    const editingStatus = this.testStatusEditing[test.id];
    if (editingStatus !== undefined && editingStatus !== null) {
      return editingStatus;
    }
    return test.status || null;
  }

  private mapComponentStatusToApiStatus(componentStatus: string): string {
    switch (componentStatus) {
      case 'passed':
        return 'PASSED';
      case 'failed':
        return 'FAILED';
      case 'na':
        return 'SKIPPED';
      case 'pending':
      default:
        return 'NOT_STARTED';
    }
  }

  private updateFailureEvidenceFileIdsAfterDeletion(deletedIndex: number): void {
    const newMap = new Map<number, number>();
    let newIndex = 0;
    this.failureEvidenceFileIds.forEach((attachmentId, oldIndex) => {
      if (oldIndex !== deletedIndex) {
        newMap.set(newIndex, attachmentId);
        newIndex++;
      }
    });
    this.failureEvidenceFileIds = newMap;
  }

  toggleRule(rule: Rule): void {
    rule.expanded = !rule.expanded;
  }

  toggleRulesSection(): void {
    this.rulesCollapsed = !this.rulesCollapsed;
  }

  confirmReview(): void {
    if (this.selectedMacroTest && this.canToggleReviewConfirmation()) {
      this.selectedMacroTest.reviewConfirmed = !this.selectedMacroTest.reviewConfirmed;
    }
  }

  getMacroTestDynamicClass(macroTest: MacroTest): string {
    switch (macroTest.result) {
      case 'PASSED': return 'test-passed';
      case 'FAILED': return 'test-failed';
      case 'SKIPPED': return 'test-na';
      case 'LIGHT_PASSED': return 'test-passed-light';
      case 'LIGHT_FAILED': return 'test-failed-light';
      case 'PENDING': return 'test-pending';
      default: return 'test-not-reviewed';
    }
  }

  triggerFileInput(testId: number): void {
    const fileInput = document.getElementById('file-' + testId) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  getTestStatusLabel(status: string | null): string {
    switch (status) {
      case 'passed': return 'Test Passed';
      case 'failed': return 'Test Failed';
      case 'na': return 'Test N/A';
      default: return 'Test Pending';
    }
  }

  getWithoutEvidenceTests(): any[] {
    return this.selectedMacroTest ? this.selectedMacroTest.individual_tests_without_evidence : [];
  }

  getWithEvidenceTests(): any[] {
    return this.selectedMacroTest ? this.selectedMacroTest.individual_tests_with_evidence : [];
  }

  areWithoutEvidenceTestsCompleted(): boolean {
    const tests = this.getWithoutEvidenceTests();
    if (tests.length === 0) return true;
    return tests.every(test =>
      test.status !== null &&
      test.status !== 'pending' &&
      !this.testStatusChanges.has(test.id)
    );
  }

  areWithoutEvidenceTestsAllPassed(): boolean {
    const tests = this.getWithoutEvidenceTests();
    if (tests.length === 0) return true;
    return tests.every(test =>
      test.status === 'passed' &&
      !this.testStatusChanges.has(test.id)
    );
  }

  areWithEvidenceTestsAllPassed(): boolean {
    const tests = this.getWithEvidenceTests();
    if (tests.length === 0) return true;
    return tests.every(test =>
      test.status === 'passed' &&
      !this.testStatusChanges.has(test.id)
    );
  }

  canToggleReviewConfirmation(): boolean {
    return this.canEditReviewCheckbox
      && this.areWithoutEvidenceTestsAllPassed()
      && this.areWithEvidenceTestsAllPassed();
  }

  passTest(): void {
    this.setMacroTestStatus('passed');
  }

  pendingTest(): void {
    this.setMacroTestStatus('pending');
  }

  failTest(): void {
    this.setMacroTestStatus('failed');
  }

  naTest(): void {
    this.setMacroTestStatus('na');
  }

  onFileSelected(event: any, testId: number): void {
    const files = Array.from(event.target.files) as File[];
    if (this.selectedMacroTest) {
      const test = this.selectedMacroTest.individual_tests.find(t => t.id === testId);
      if (test) {
        test.evidence_files = files;
        
        if (test.projectTestlistStepId && files.length > 0) {
          for (let file of files) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('data', test.projectTestlistStepId.toString());

            this.projectsService.createProjectTestListSteps(
              Number(this.projectId),
              this.selectedMacroTest.projectTestlistId!,
              test.projectTestlistStepId,
              formData
            ).subscribe({
              next: (response) => {
                this.alertNotificationService.success(`Evidence file "${file.name}" uploaded for this test`);
              },
              error: (error) => {
                this.alertNotificationService.error(`Error uploading evidence file "${file.name}"`);
                console.error('Error uploading evidence:', error);
              }
            });
          }
        }
      }
    }
  }

  removeFile(testId: number, fileIndex: number): void {
    if (this.selectedMacroTest) {
      const test = this.selectedMacroTest.individual_tests.find(t => t.id === testId);
      if (test && test.evidence_files) {
        test.evidence_files.splice(fileIndex, 1);
      }
    }
  }

  getSelectedBatchControl(): FormControl { 
    return this.form.get('selectedBatch') as FormControl; 
  }

  getSelectedChecklistControl(): FormControl { 
    return this.form.get('selectedChecklist') as FormControl; 
  }
  
  getSelectedTesterControl(): FormControl { 
    return this.form.get('selectedTester') as FormControl; 
  }
  
  isBatchSelected(): boolean {
    return this.selectedBatchId !== null && this.selectedBatchId > 0;
  }
  
  canUploadFailureFile(): boolean {
    return this.failureEvidenceFiles.length === 0 && this.failureExistingEvidenceFiles.length === 0;
  }

  onFailureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      if (!this.canUploadFailureFile()) {
        this.alertNotificationService.warning('Remove the existing evidence file before uploading a new one');
        input.value = '';
        return;
      }
      const file = input.files[0];
      this.failureEvidenceFiles.push(file);
      input.value = '';
    }
  }

  removeFailureFile(fileIndex: number): void {
    this.selectedFileToDelete = fileIndex;
    this.confirmDialogAction = 'deleteFailure';
    this.confirmationDialogService.open(
      'Are you sure you want to delete this evidence file?',
      () => this.onConfirmDialog()
    );
  }

  onConfirmDialog(): void {
    if (this.confirmDialogAction === 'deleteGlobal') {
      this.onDeleteFileConfirmed();
    } else if (this.confirmDialogAction === 'deleteFailure') {
      if (this.isFailureFileDeletion) {
        if (this.selectedFileToDelete !== null && this.failedTest && this.failedTest.projectIssueId) {
          const fileToDelete = this.failureExistingEvidenceFiles[this.selectedFileToDelete];
          const attachmentId = this.failureExistingEvidenceFileIds.get(this.selectedFileToDelete);

          if (!attachmentId) {
            this.failureExistingEvidenceFiles.splice(this.selectedFileToDelete, 1);
            this.updateFailureExistingEvidenceFileIdsAfterDeletion(this.selectedFileToDelete);
            this.alertNotificationService.success(`File "${fileToDelete.fileName}" removed`);
          } else {
            this.projectsService.deleteProjectIssuesEvidences(
              Number(this.projectId),
              this.failedTest.projectIssueId,
              attachmentId
            ).subscribe({
              next: (response) => {
                this.failureExistingEvidenceFiles.splice(this.selectedFileToDelete!, 1);
                this.updateFailureExistingEvidenceFileIdsAfterDeletion(this.selectedFileToDelete!);
                this.alertNotificationService.success(`File "${fileToDelete.fileName}" deleted successfully`);
              },
              error: (error) => {
                this.alertNotificationService.error(`Error deleting file "${fileToDelete.fileName}"`);
                console.error('Error deleting file:', error);
              }
            });
          }
        }
      } else {
        if (this.selectedFileToDelete !== null && this.failedTest && this.failedTest.projectTestlistStepId && this.selectedMacroTest?.projectTestlistId) {
          const fileToDelete = this.failureEvidenceFiles[this.selectedFileToDelete];
          const attachmentId = this.failureEvidenceFileIds.get(this.selectedFileToDelete);

          if (!attachmentId) {
            this.failureEvidenceFiles.splice(this.selectedFileToDelete, 1);
            this.updateFailureEvidenceFileIdsAfterDeletion(this.selectedFileToDelete);
            this.alertNotificationService.success(`File "${fileToDelete.name}" deleted successfully`);
          } else {
            this.projectsService.deleteProjectTestListSteps(
              Number(this.projectId),
              this.selectedMacroTest!.projectTestlistId!,
              this.failedTest.projectTestlistStepId,
              attachmentId
            ).subscribe({
              next: (response) => {
                this.failureEvidenceFiles.splice(this.selectedFileToDelete!, 1);
                this.updateFailureEvidenceFileIdsAfterDeletion(this.selectedFileToDelete!);
                this.alertNotificationService.success(`File "${fileToDelete.name}" deleted successfully`);
              },
              error: (error) => {
                this.alertNotificationService.error(`Error deleting file "${fileToDelete.name}"`);
                console.error('Error deleting file:', error);
              }
            });
          }
        }
      }
    }

    this.closeConfirmDialog();
  }

  closeConfirmDialog(): void {
    this.selectedFileToDelete = null;
    this.confirmDialogAction = null;
    this.isFailureFileDeletion = false;
  }

  triggerFailureFileInput(): void {
    const fileInput = document.getElementById('failure-evidence-file') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  private loadFailureExistingEvidences(projectIssueId: number): void {
    if (!this.projectId) return;

    this.isLoadingFailureEvidences = true;
    
    this.projectsService.listProjectIssuesEvidences(Number(this.projectId), projectIssueId)
      .subscribe({
        next: (response) => {
          const evidences = response.data?.attachments || [];
          
          this.failureExistingEvidenceFiles = evidences.map((evidence: any) => ({
            attachmentId: evidence.attachmentId,
            fileName: evidence.fileNameOriginal,
            url: evidence.url,
            fileSize: evidence.fileSize,
            uploadedAt: evidence.uploadedAt,
            isServerFile: true
          }));
          
          this.failureExistingEvidenceFileIds.clear();
          evidences.forEach((evidence: any, index: number) => {
            this.failureExistingEvidenceFileIds.set(index, evidence.attachmentId);
          });
          
          this.isLoadingFailureEvidences = false;
        },
        error: (error) => {
          console.error('Error loading failure evidence files:', error);
          this.failureExistingEvidenceFiles = [];
          this.isLoadingFailureEvidences = false;
        }
      });
  }

  private loadFailureIssueDetails(projectIssueId: number): void {
    if (!this.projectId) return;
    
    this.projectsService.detailProjectIssue(Number(this.projectId), projectIssueId)
      .subscribe({
        next: (response) => {
          const description = response.data?.projectIssue?.description || '';
          this.failureCommentForm.patchValue({
            failureComment: description
          });
        },
        error: (error) => {
          console.error('Error loading issue details:', error);
        }
      });
  }

  confirmTestFailure(): void {
    if (!this.failureCommentForm.valid) {
      this.alertNotificationService.error('Please fill in all required fields in the failure modal');
      return;
    }
    if (!this.failedTest || this.pendingFailureTestId === null) {
      return;
    }

    const failureComment = this.failureCommentForm.get('failureComment')?.value;

    if (this.failedTest.projectIssueId && this.failedTestWasAlreadyFailed) {
      const existingIssueId = this.failedTest.projectIssueId;

      const updateIssue = () => {
        this.projectsService.updateProjectIssue(Number(this.projectId), existingIssueId, { description: failureComment.trim() })
          .subscribe({
            next: () => {
              this.finalizeTestFailureConfirmation('Test issue updated and evidence uploaded');
            },
            error: (error) => {
              this.alertNotificationService.error('Error updating project issue');
              console.error('Error updating project issue:', error);
            }
          });
      };

      if (this.failureEvidenceFiles.length > 0) {
        this.uploadFailureEvidenceFiles(existingIssueId).subscribe({
          next: () => updateIssue(),
          error: (error) => {
            this.alertNotificationService.error('Error uploading evidence file(s)');
            console.error('Error uploading evidence files:', error);
          }
        });
      } else {
        updateIssue();
      }
    } else {
      const issuePayload = {
        projectTestlistStepId: this.failedTest.projectTestlistStepId,
        description: failureComment.trim()
      };

      this.projectsService.createProjectIssue(Number(this.projectId), issuePayload)
        .subscribe({
          next: (issueResponse) => {
            const projectIssueId = issueResponse.data?.projectIssue.projectIssueId;

            if (!projectIssueId) {
              this.alertNotificationService.error('Failed to create project issue: Missing project issue ID');
              return;
            }

            this.failedTest.projectIssueId = projectIssueId;

            if (this.failureEvidenceFiles.length > 0) {
              this.uploadFailureEvidenceFiles(projectIssueId).subscribe({
                next: () => {
                  this.finalizeTestFailureConfirmation('Test marked as failed with issue created and evidence uploaded');
                },
                error: (error) => {
                  this.alertNotificationService.error('Error uploading evidence file(s)');
                  console.error('Error uploading evidence files:', error);
                }
              });
            } else {
              this.finalizeTestFailureConfirmation('Test marked as failed with issue created');
            }
          },
          error: (error) => {
            this.alertNotificationService.error('Error creating project issue');
            console.error('Error creating project issue:', error);
          }
        });
    }
  }

  private finalizeTestFailureConfirmation(successMessage: string): void {
    if (!this.failedTestWasAlreadyFailed) {
      this.setTestStatusFailedFromModal(this.pendingFailureTestId!);
    }
    this.failureModalConfirmed = true;

    this.closeTestFailureModal(false);
    this.onTestlistUpdated.emit();

    this.alertNotificationService.success(successMessage);
  }

  private uploadFailureEvidenceFiles(projectIssueId: number): Observable<any> {
    if (!this.failureEvidenceFiles.length || !this.projectId) {
      return of(void 0);
    }

    const uploads$ = this.failureEvidenceFiles.map((file, fileIndex) => {
      const formData = new FormData();
      formData.append('file', file);

      return this.projectsService.createProjectIssuesEvidences(
        Number(this.projectId),
        projectIssueId,
        formData
      ).pipe(
        tap((response) => {
          if (response.data?.attachmentId) {
            this.failureEvidenceFileIds.set(fileIndex, response.data.attachmentId);
          }
        })
      );
    });

    return forkJoin(uploads$);
  }

  closeTestFailureModal(resetConfirmed: boolean = true): void {
    if (this.pendingFailureTestId !== null) {
      if (resetConfirmed && !this.failureModalConfirmed) {
        delete this.testStatusEditing[this.pendingFailureTestId];
        this.testStatusChanges.delete(this.pendingFailureTestId);
      }
    }

    this.testFailureModalService.close();
    this.failedTest = null;
    this.failureCommentForm.reset();
    this.failureEvidenceFiles = [];
    this.failureEvidenceFileIds = new Map();
    this.failureExistingEvidenceFiles = [];
    this.failureExistingEvidenceFileIds = new Map();
    this.isLoadingFailureEvidences = false;
    this.failedTestWasAlreadyFailed = false; 
    if (resetConfirmed) {
      this.failureModalConfirmed = false;
    }
    this.pendingFailureTestId = null;
  }

  private openTestFailureModal(): void {
    const context: TestFailureModalContext = {
      getFailedTest: () => this.failedTest,
      getFailureCommentControl: () => this.getFailureCommentControl(),
      getFailureEvidenceFiles: () => this.failureEvidenceFiles,
      getFailureExistingEvidenceFiles: () => this.failureExistingEvidenceFiles,
      isFailureModalValid: () => this.isFailureModalValid(),
      onClose: (resetConfirmed?: boolean) => this.closeTestFailureModal(resetConfirmed),
      onConfirm: () => this.confirmTestFailure(),
      onTriggerFileInput: () => this.triggerFailureFileInput(),
      onFileSelected: (event: Event) => this.onFailureFileSelected(event),
      onRemoveFailureFile: (index: number) => this.removeFailureFile(index),
      onDownloadFailureFile: (file: any) => this.downloadFailureFile(file),
      onRemoveExistingFailureFile: (index: number) => this.removeExistingFailureFile(index),
      canDeleteFiles: this.canDeleteFailureFiles,
      canUploadFailureFile: () => this.canUploadFailureFile(),
      canCloneIssue: () => this.canCloneIssue(),
      onCloneIssue: () => this.openCloneIssueModal()
    };

    this.testFailureModalService.open(context);
  }

  canCloneIssue(): boolean {
    return this.canEditTestlist
      && this.failedTestWasAlreadyFailed
      && !!this.failedTest?.projectIssueId
      && !!this.failedTest?.projectTestlistStepId
      && !!this.selectedMacroTest?.projectTestlistId;
  }

  openCloneIssueModal(): void {
    if (!this.canCloneIssue() || !this.projectId) {
      return;
    }

    this.cloneIssueSource = {
      projectTestlistId: this.selectedMacroTest!.projectTestlistId!,
      projectTestlistStepId: this.failedTest.projectTestlistStepId,
      testTitle: this.failedTest.title ?? ''
    };

    this.closeTestFailureModal(false);

    this.cloneIssueModalService.open({
      testTitle: this.cloneIssueSource.testTitle,
      steps: [],
      isLoading: true,
      loadError: false,
      isSaving: false,
      onClose: () => this.closeCloneIssueModal(),
      onConfirm: (destinationProjectTestlistStepIds: number[]) =>
        this.confirmCloneIssue(destinationProjectTestlistStepIds)
    });

    this.loadCloneIssueDestinations();
  }

  closeCloneIssueModal(): void {
    this.cloneIssueModalService.close();
    this.cloneIssueSource = null;
  }

  private loadCloneIssueDestinations(): void {
    if (!this.projectId || !this.cloneIssueSource) {
      return;
    }

    const { projectTestlistId, projectTestlistStepId } = this.cloneIssueSource;

    this.projectsService.listProjectTestlistChecklistCloneIssue(
      Number(this.projectId),
      projectTestlistId,
      projectTestlistStepId
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.cloneIssueModalService.update({
            steps: this.mapCloneIssueDestinations(response),
            isLoading: false,
            loadError: false
          });
        },
        error: (error) => {
          console.error('Error loading clone issue destinations:', error);
          this.cloneIssueModalService.update({ isLoading: false, loadError: true });
        }
      });
  }

  private mapCloneIssueDestinations(response: any): CloneIssueDestinationStep[] {
    const rawSteps: any[] = response?.data?.projectTestlistSteps ?? [];

    return rawSteps
      .map((rawStep): CloneIssueDestinationStep | null => {
        const projectTestlistStepId = Number(rawStep?.projectTestlistStepId);
        if (!projectTestlistStepId) {
          return null;
        }

        const testlistStep = rawStep?.testlistStep;
        const text: string = testlistStep?.text ?? '';

        return {
          projectTestlistStepId,
          label: text.trim() || `Step ${projectTestlistStepId}`,
          position: testlistStep?.position ?? null,
          type: testlistStep?.type ?? null,
          issuesCount: rawStep?.projectIssues?.length ?? 0
        };
      })
      .filter((step): step is CloneIssueDestinationStep => step !== null)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }

  private confirmCloneIssue(destinationProjectTestlistStepIds: number[]): void {
    if (!this.projectId || !this.cloneIssueSource || !destinationProjectTestlistStepIds.length) {
      return;
    }

    const { projectTestlistId, projectTestlistStepId } = this.cloneIssueSource;
    this.cloneIssueModalService.update({ isSaving: true });

    this.projectsService.createProjectTestlistChecklistCloneIssue(
      Number(this.projectId),
      projectTestlistId,
      projectTestlistStepId,
      { destinationProjectTestlistStepIds }
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeCloneIssueModal();
          this.alertNotificationService.success(
            `Issue cloned to ${destinationProjectTestlistStepIds.length} step(s)`
          );
          this.onTestlistUpdated.emit();

          const macroTest = this.selectedMacroTest;
          if (macroTest?.projectTestlistId) {
            this.refreshMacroTestStatus(macroTest);
            this.loadProjectTestListSteps(
              Number(this.projectId),
              macroTest.projectTestlistId,
              macroTest,
              false
            );
          }
        },
        error: (error) => {
          this.cloneIssueModalService.update({ isSaving: false });
          this.alertNotificationService.error('Error cloning the issue');
          console.error('Error cloning issue:', error);
        }
      });
  }


  downloadGlobalEvidenceFile(file: any): void {
    if (!file.url) return;

    this.projectsService.GET(file.url).subscribe({
      next: (response: any) => {
        window.open(response.data.attachment.publicUrl, '_blank');
      },
      error: (error: any) => {
        this.alertNotificationService.error('Error downloading file');
        console.error('Error:', error);
      }
    });
  }

  downloadFailureFile(file: any): void {
    if (!this.projectId || !file.url) return;
    
    this.projectsService.GET(file.url).subscribe({
      next: (response: any) => {
        window.open(response.data.attachment.publicUrl, '_blank');
      }, 
      error: (error: any) => {
        this.alertNotificationService.error('Error downloading file');
        console.error('Error:', error);
      }
    });
  }

  removeExistingFailureFile(fileIndex: number): void {
    if (this.failedTest && this.selectedMacroTest?.projectTestlistId) {
      this.selectedFileToDelete = fileIndex;
      this.confirmDialogAction = 'deleteFailure';
      this.isFailureFileDeletion = true; 
      this.confirmationDialogService.open(
        'Are you sure you want to delete this uploaded evidence file?',
        () => this.onConfirmDialog()
      );
    }
  }

  private updateFailureExistingEvidenceFileIdsAfterDeletion(deletedIndex: number): void {
    const newMap = new Map<number, number>();
    let newIndex = 0;
    this.failureExistingEvidenceFileIds.forEach((attachmentId, oldIndex) => {
      if (oldIndex !== deletedIndex) {
        newMap.set(newIndex, attachmentId);
        newIndex++;
      }
    });
    this.failureExistingEvidenceFileIds = newMap;
  }

  getFailureCommentControl(): FormControl {
    return this.failureCommentForm.get('failureComment') as FormControl;
  }

  isFailureModalValid(): boolean {
    return this.failureCommentForm.valid && 
           this.failedTest !== null && 
           this.pendingFailureTestId !== null;
  }

  onRoundStateChanged(event: {roundId: string, stateId: string, status: string}): void {
    if (event.status === 'completed') {
      this.alertNotificationService.success(`${event.stateId} completed successfully`);
    }
    this.saveRoundStateForCurrentBatch();
  }

  onRoundCompleted(event: {roundId: string, completedAt: Date}): void {
    this.alertNotificationService.success(`${event.roundId} completed at ${event.completedAt.toLocaleString()}`);
    this.saveRoundStateForCurrentBatch();
    this.onTestlistUpdated.emit();
  }

  onRoundDataUpdated(roundData: any): void {
    this.saveRoundStateForCurrentBatch(roundData);
  }

  private saveRoundStateForCurrentBatch(roundData?: any): void {
    if (this.selectedBatchId) { 
      const batch = this.batches.find(b => b.key === this.selectedBatchId);
      if (batch) {
        batch.roundData = roundData || batch.roundData;
      }
    }
  }

  getCurrentBatchRoundData(): any {
    if (this.selectedBatchId) {
      const batch = this.batches.find(b => b.key === this.selectedBatchId);
      return batch?.roundData || null;
    }
    return null;
  }

  private loadEvidenceFilesForTestlist(projectTestlistId: number): void {
    if (!this.projectId) return;

    this.projectsService.listProjectTestlistEvidences(Number(this.projectId), projectTestlistId)
      .subscribe({
        next: (response) => {
          const evidences = response.data?.attachments || [];
          
          this.globalEvidenceFiles = evidences.map((evidence: any) => ({
            attachmentId: evidence.attachmentId,
            fileName: evidence.fileNameOriginal,
            url: evidence.url,
            isServerFile: true
          }));
          this.globalEvidenceFileIds.clear();
          evidences.forEach((evidence: any, index: number) => {
            this.globalEvidenceFileIds.set(index, evidence.attachmentId);
          });
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading evidence files');
          console.error('Error loading evidences:', error);
          this.globalEvidenceFiles = [];
        }
      });
  }

  onGlobalFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length && this.selectedMacroTest?.projectTestlistId) {
      const files = Array.from(input.files);
      
      for (let file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('data', this.selectedMacroTest.projectTestlistId.toString());

        this.projectsService.createProjectTestListEvidences(
          Number(this.projectId),
          this.selectedMacroTest.projectTestlistId,
          formData
        ).subscribe({
          next: (response) => {
            const newEvidence = {
              attachmentId: response.data?.attachmentId,
              fileName: file.name,
              fileSize: file.size,
              uploadedAt: new Date(),
              isServerFile: true
            };
            this.globalEvidenceFiles.push(newEvidence);
            
            if (response.data?.attachmentId) {
              this.globalEvidenceFileIds.set(
                this.globalEvidenceFiles.length - 1,
                response.data.attachmentId
              );
            }
            
            this.alertNotificationService.success(`File "${file.name}" uploaded successfully`);
          },
          error: (error) => {
            this.alertNotificationService.error(`Error uploading file "${file.name}"`);
            console.error('Error uploading file:', error);
          }
        });
      }

      input.value = '';
    } else if (input.files && input.files.length) {
      this.alertNotificationService.warning('Please select a testlist first');
    }
  }

  removeGlobalFile(fileIndex: number): void {
    if (!this.canUploadEvidence) return;
    this.selectedFileToDelete = fileIndex;
    this.confirmDialogAction = 'deleteGlobal';
    this.confirmationDialogService.open(
      'Are you sure you want to delete this file?',
      () => this.onDeleteFileConfirmed()
    );
  }

  onDeleteFileConfirmed(): void {
    if (this.selectedFileToDelete !== null && this.selectedMacroTest?.projectTestlistId) {
      const fileToDelete = this.globalEvidenceFiles[this.selectedFileToDelete];
      const attachmentId = this.globalEvidenceFileIds.get(this.selectedFileToDelete);
      
      if (!attachmentId) {
        this.alertNotificationService.error('Unable to delete file: attachment ID not found');
        this.closeDeleteConfirmation();
        return;
      }

      this.projectsService.deleteProjectTestListEvidences(
        Number(this.projectId),
        this.selectedMacroTest.projectTestlistId,
        attachmentId
      ).subscribe({
        next: (response) => {
          this.globalEvidenceFiles.splice(this.selectedFileToDelete!, 1);
          this.updateGlobalEvidenceFileIdsAfterDeletion(this.selectedFileToDelete!);
          this.alertNotificationService.success(`File "${fileToDelete.fileName}" deleted successfully`);
        },
        error: (error) => {
          this.alertNotificationService.error(`Error deleting file "${fileToDelete.fileName}"`);
          console.error('Error deleting file:', error);
        }
      });
    }
    this.closeDeleteConfirmation();
  }

  closeDeleteConfirmation(): void {
    this.selectedFileToDelete = null;
  }

  private updateGlobalEvidenceFileIdsAfterDeletion(deletedIndex: number): void {
    const newMap = new Map<number, number>();
    let newIndex = 0;
    this.globalEvidenceFileIds.forEach((attachmentId, oldIndex) => {
      if (oldIndex !== deletedIndex) {
        newMap.set(newIndex, attachmentId);
        newIndex++;
      }
    });
    this.globalEvidenceFileIds = newMap;
  }

  triggerGlobalFileInput(): void {
    const fileInput = document.getElementById('global-evidence-file') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  submitForm(): void {
    const macroTest = this.selectedMacroTest;
    if (!macroTest || !macroTest.projectTestlistId) {
      this.alertNotificationService.warning('Please select a testlist first');
      return;
    }

    const desiredResult = macroTest.stagedResult ?? macroTest.result ?? null;
    const desiredReviewed = macroTest.reviewConfirmed ?? false;
    const lastSavedResult = macroTest.apiResult ?? null;
    const lastSavedReviewed = macroTest.reviewedSynced ?? false;

    if (desiredResult === lastSavedResult && desiredReviewed === lastSavedReviewed) {
      this.alertNotificationService.success('Testlist is already up to date');
      return;
    }

    const projectTestlistId = macroTest.projectTestlistId;
    const payload = {
      result: desiredResult ?? undefined,
      reviewed: desiredReviewed
    };

    this.projectsService.updateProjectTestlistReviewStatus(
      Number(this.projectId),
      projectTestlistId,
      payload
    ).subscribe({
      next: () => {
        macroTest.apiResult = desiredResult ?? undefined;
        macroTest.result = desiredResult;
        macroTest.status = this.mapApiResultToComponentStatus(desiredResult ?? '');
        macroTest.reviewedSynced = desiredReviewed;
        macroTest.stagedResult = null;

        if (desiredResult !== lastSavedResult) {
          this.refreshChecklistOptions();
        }

        this.alertNotificationService.success('Testlist review status updated successfully');
        this.onTestlistUpdated.emit();
      },
      error: (error) => {
        this.alertNotificationService.error('Error updating testlist review status');
        console.error('Error updating testlist review status:', error);
      }
    });
  }
}
